import React, { useMemo, useEffect, useRef, useState } from "react";
import { Editor } from "@monaco-editor/react";
import { supabase } from "../supabaseClient";
import CircuitFileList from "./CircuitFileList";
// import CircuitBenchmarkForm from "./CircuitBenchmarkForm";
import CircuitBenchmark from "./CircuitBenchmark";

const DEFAULT_CODE = "package circuits; // Don'r remove this line";
const DEFAULT_EXTENSION = 'go'

const sort_order = {
    code: 0,
    input: 1
};

const CircuitEditor = React.memo(function CircuitEditor({ circuit_id, circuitFiles, onError }) {

    const [activeFileIndex, setActiveFileIndex] = useState(() => {
        const savedValue = localStorage.getItem('activeFileIndex');
        return savedValue !== null ? parseInt(savedValue, 10) : -1;
    });
    const [code, setCode] = useState(DEFAULT_CODE);
    const [files, setFiles] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    // Ref for the Monaco editor instance
    const editorRef = useRef(null);

    function handleEditorDidMount(editor, monaco) {
        editorRef.current = editor;
    }


    useEffect(() => {
        setFiles(circuitFiles.sort((a, b) => sort_order[a.type] - sort_order[b.type]));
        setActiveFileIndex(activeFileIndex);
        setCode(circuitFiles[activeFileIndex]?.content || DEFAULT_CODE);

    }, [circuitFiles]);

    // Memoize current language
    const currentLanguage = useMemo(() => {
        return files[activeFileIndex]?.extension || "go";
    }, [files, activeFileIndex]);


    // Handle file selection
    const handleFileClick = (id) => {

        const file = files.filter(f => f.id === id)[0];
        if (!file) return;
        const index = files.findIndex(f => f.id === id);

        setActiveFileIndex(index);
        localStorage.setItem('activeFileIndex', index);
        setCode(file.content);
    };

    const handleCodeChange = (newValue) => {
        setCode(newValue);
        if (activeFileIndex !== null && files.length > activeFileIndex) {
            files[activeFileIndex].content = newValue;
            setHasUnsavedChanges(true);
        }
    }

    const createNewFile = (file_type) => {
        let newFile = {
            content: file_type === 'input' ? '{}' : DEFAULT_CODE,
            name: 'Untitled',
            extension: file_type === 'input' ? 'json' : DEFAULT_EXTENSION,
            circuit_id: circuit_id,
            id: crypto.randomUUID(),
            type: file_type
        };

        const updatedFiles = [...files, newFile].sort((a, b) => sort_order[a.type] - sort_order[b.type]);

        setFiles(updatedFiles);
        const numofcodefiles = updatedFiles.filter(f => f.type === 'code').length;
        setActiveFileIndex((file_type === 'code' ? numofcodefiles : updatedFiles.length) - 1);
        setCode(newFile.content);

        circuitFiles.push(newFile);
        setHasUnsavedChanges(true);
    }

    const handleFileNameChange = (e) => {
        setHasUnsavedChanges(true);
        const newName = e.target.value;
        const [name, extension] = newName.split('.');
        const updatedFiles = [...files];
        updatedFiles[activeFileIndex] = {
            ...updatedFiles[activeFileIndex],
            name: name || '',
            extension: extension || '',
        };
        setFiles(updatedFiles);
        circuitFiles[activeFileIndex] = updatedFiles[activeFileIndex];
    }

    const handleSave = async () => {
        setIsLoading(true);

        if (files.length < activeFileIndex) {
            createNewFile();
        }

        let file = files[activeFileIndex];

        if (!file.hasOwnProperty("created_at")) {
            const { data, error } = await supabase
                .from('circuit_files')
                .insert([file]).select();
            if (error) {
                onError(error.message);
            }
        } else {
            file.updated_at = new Date();
            const { data, error } = await supabase
                .from('circuit_files')
                .update(file)
                .eq('id', file.id)
                .select();
            if (error) {
                onError(error.message);
            }
        }
        setIsLoading(false);
        setHasUnsavedChanges(false);
    }

    const deleteFile = async () => {
        if (activeFileIndex === null || activeFileIndex >= files.length) {
            console.log("deleting non-existent file");
            return;
        }

        if (!window.confirm("Are you sure you want to delete this file? This action is irreversible.")) {
            return;
        }


        setIsLoading(true);
        const oldFileId = files[activeFileIndex].id;
        const updatedFiles = files.filter(a => a.id !== oldFileId);

        const { error } = await supabase
            .from('circuit_files')
            .delete()
            .eq('id', oldFileId);

        if (error) {
            onError(error.message);
            setIsLoading(false);
            return;
        } else {

            setFiles(updatedFiles);

            // Set activeFileIndex to the next valid index, or null if none exists
            let newActiveIndex = activeFileIndex;
            if (newActiveIndex >= updatedFiles.length) {
                newActiveIndex = updatedFiles.length > 0 ? updatedFiles.length - 1 : null;
            }

            setActiveFileIndex(newActiveIndex);
            setCode(updatedFiles[newActiveIndex].content);
            setIsLoading(false);
        }
    }

    return (
        <div className="box columns m-0 p-0" style={{ overflow: 'hidden', height: '800px' }}>
            <CircuitFileList
                circuit_files={files.filter(f => f.type === "code")}
                input_files={files.filter(f => f.type === "input")}
                activeFileIndex={activeFileIndex}
                onFileClick={handleFileClick}
                onCreateNew={createNewFile}
            />
            {/* Editor */}
            <div className="column">
                {activeFileIndex >= 0 ? (
                    <>
                        <nav className="navbar is-white p-2 mb-1" style={{ borderBottom: '1px solid #e5e7eb' }}>
                            <div className="navbar-start">
                                <div className="control has-icons-left">
                                    <input type='text' className="input is-static is-small"
                                        value={files[activeFileIndex]?.name + '.' + files[activeFileIndex]?.extension}
                                        onChange={handleFileNameChange}
                                    />
                                    <span className="icon is-left">
                                        <i className="fas fa-pencil"></i>
                                    </span>
                                </div>
                            </div>

                            <div className="navbar-end">
                                {hasUnsavedChanges && (
                                    <span className="tag is-warning is-dark my-2 mr-2">
                                        Unsaved changes
                                    </span>
                                )}

                                <button
                                    className="button is-small is-outlined is-dark mr-2"
                                    disabled={!hasUnsavedChanges}
                                    onClick={handleSave}
                                >
                                    <span className="icon">
                                        <i className="fa-solid fa-floppy-disk" />
                                    </span>
                                    <span>Save</span>
                                </button>

                                <button
                                    className="button is-dark is-small"
                                    onClick={deleteFile}
                                >
                                    <span className="icon">
                                        <i className="fa-solid fa-trash" />
                                    </span>
                                    <span>Delete</span>
                                </button>
                            </div>
                        </nav>
                        <Editor
                            height="100%"
                            language={currentLanguage}
                            value={code}
                            defaultValue="// Create new file"
                            onChange={handleCodeChange}
                            onMount={handleEditorDidMount}
                            theme="vs-light"
                            options={{
                                minimap: { enabled: false },
                                fontSize: 12,
                                automaticLayout: true,
                                domReadOnly: files.length === 0,
                                readOnly: files.length == 0,
                            }}
                        />
                    </>
                ) : (
                    <div className="columns is-vcentered" style={{ height: "100%", width: "100%", textAlign: "center" }}>
                        <div className="column">
                            <span className="icon is-large">
                                <i className="fa-solid fa-file-code fa-3x mb-3" />
                            </span>
                            <p>Select or create a file to edit</p>
                        </div>
                    </div>
                )}
            </div>

            {/* Benchmark form*/}
            <CircuitBenchmark
                circuit_id={circuit_id}
                circuitFiles={circuitFiles}
                onError={onError}
                disabled={hasUnsavedChanges} />
        </div >
    );
});

export default React.memo(CircuitEditor);